// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the first seconds after the app loads belong to what is on screen. A load (or a reload
// onto a new build, when the Rail follows the main window) used to fire 40-50 REST requests at
// once from every window, and the API's per-person limits answered the tail with 429s for half a
// minute. Background work nobody is looking at yet (ClickUp reference data, mail prefetches, the
// corner's queue, calendar-driven presence) waits until the page is STARTUP_SETTLE_MS old, plus a
// per-task delay and random jitter so windows and devices that started together spread out, and
// then for an idle moment. Later in the page's life the same calls run straight away.

export const STARTUP_SETTLE_MS = 4000;
/** Longest an idle callback may wait once the delay is over. */
const IDLE_TIMEOUT_MS = 2000;

export interface AfterStartupOptions {
	/** Extra wait after the settle time. */
	delayMs?: number;
	/** Up to this much more, picked at random per call. */
	jitterMs?: number;
}

/** How long this page has been running (performance.now() counts from navigation start). */
function pageAgeMs(): number {
	return typeof performance !== 'undefined' && typeof performance.now === 'function'
		? performance.now()
		: Number.POSITIVE_INFINITY;
}

/** The page is still in its first seconds. */
export function isStartingUp(age = pageAgeMs()): boolean {
	return age < STARTUP_SETTLE_MS;
}

/** How long a task must still wait; 0 once the page has settled past the task's own delay. */
export function startupDelayMs(options: AfterStartupOptions = {}, age = pageAgeMs(), random = Math.random): number {
	const target = STARTUP_SETTLE_MS + (options.delayMs ?? 0) + random() * (options.jitterMs ?? 0);
	return Math.max(0, Math.round(target - age));
}

function whenIdle(task: () => void): () => void {
	const idle = typeof window !== 'undefined' ? window.requestIdleCallback : undefined;
	if (typeof idle === 'function') {
		const handle = idle.call(window, task, {timeout: IDLE_TIMEOUT_MS});
		return () => window.cancelIdleCallback?.(handle);
	}
	const timer = setTimeout(task, 0);
	return () => clearTimeout(timer);
}

/**
 * Runs `task` once the app has settled: at once (synchronously) when the page is past its
 * startup and the task's delay, otherwise after the remaining wait and an idle moment.
 * Returns a function that cancels a task still waiting.
 */
export function afterStartup(task: () => void, options: AfterStartupOptions = {}): () => void {
	const wait = startupDelayMs(options);
	if (wait === 0) {
		task();
		return () => {};
	}
	let cancelled = false;
	let cancelIdle: (() => void) | null = null;
	const timer = setTimeout(() => {
		cancelIdle = whenIdle(() => {
			if (!cancelled) task();
		});
	}, wait);
	return () => {
		cancelled = true;
		clearTimeout(timer);
		cancelIdle?.();
	};
}
